
<template>
  <ListComponent title="Outbound Integration Logs">
    <template #list>
      <v-row class="ma-0">
        <v-col cols="4" class="px-0 pt-0 pb-2 d-flex align-center">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchLogs"
            class="mr-2"
          ></v-text-field>
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                icon
                v-on="on"
                v-bind="attrs"
                @click="showFiltersDialog = true"
                class="mx-2"
              >
                <v-badge
                  dot
                  color="primary"
                  :value="Object.keys(filters).length"
                >
                  <v-icon>mdi-filter</v-icon>
                </v-badge>
              </v-btn>
            </template>

            <span v-if="Object.keys(filters).length == 0">Filters</span>

            <v-row v-else no-gutters class="ma-0">
              <v-col cols="12" class="font-weight-bold">
                Applied Filters
              </v-col>

              <v-col cols="12">
                <span
                  v-for="(value, key) in filters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>
          <Columns :headers="headers" :gridColInstance="gridColumnApi" />
        </v-col>

        <v-col cols="4" class="px-0 pt-0 pb-2">
          <v-tabs height="35" v-model="tab" centered>
            <v-tabs-slider></v-tabs-slider>
            <v-tab> Customer Invoice </v-tab>
            <v-tab> Payment Receipt </v-tab>
            <v-tab> Sub Hire </v-tab>
            <v-tab> Credit Note </v-tab>
             <v-tab> Debit Note </v-tab>
            <v-tab> SAP Bonus</v-tab>
            <v-tab> TOS Control Tower</v-tab>
            <v-tab> CODECO TOS Integration Log</v-tab>
            <v-tab> Control Tower Dashboard</v-tab>
          </v-tabs>
        </v-col>

        <v-col
          cols="4"
          class="px-0 pt-0 pb-2 d-flex justify-end align-center"
        >
          <v-tooltip top v-if="tab === 2">
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                color="secondary"
                small
                class="mr-3"
                v-on="on"
                v-bind="attrs"
                @click="showExportDialog = true"
              >
                <v-icon small class="mr-1">mdi-microsoft-excel</v-icon>
                Export
              </v-btn>
            </template>
            <span>Download Sub Hire Integration Logs in XLSX format</span>
          </v-tooltip>

          <Pagination
            v-if="totalItems > 0"
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-row>

      <v-dialog v-model="showExportDialog" max-width="420" persistent>
        <v-card>
          <v-card-title class="primary py-2">
            <span class="text-subtitle-1 font-weight-medium white--text">Export Sub Hire Logs</span>
            <v-spacer />
            <v-btn icon dark small @click="showExportDialog = false">
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-4 pb-0">
            <v-row>
              <v-col cols="6" class="Form-Columns-Dense">
                <DateField
                  :inputProps="{
                    dense: true,
                    label: 'From Date',
                    'hide-details': 'auto',
                    clearable: true,
                  }"
                  v-model="exportDateFrom"
                />
              </v-col>
              <v-col cols="6" class="Form-Columns-Dense">
                <DateField
                  :inputProps="{
                    dense: true,
                    label: 'To Date',
                    'hide-details': 'auto',
                    clearable: true,
                  }"
                  v-model="exportDateTo"
                />
              </v-col>
              <v-col cols="12" class="Form-Columns-Dense">
                <span class="caption grey--text">Leave blank to export all records (with current filters applied).</span>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="justify-end pb-3 pr-4">
            <v-btn depressed @click="showExportDialog = false">Cancel</v-btn>
            <v-btn
              color="primary"
              depressed
              :loading="exporting"
              @click="exportData"
            >
              <v-icon small class="mr-1">mdi-microsoft-excel</v-icon>
              Download
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-col cols="12" class="pa-0">
        <AgGridVue
          style="width: 100%; height: calc(100vh - 172px)"
          class="ag-theme-alpine"
          id="myGrid"
          :columnDefs="headersForTable"
          :context="context"
          @grid-ready="onGridReady"
          @columnMoved="resizeGrid"
          @columnVisible="resizeGrid"
          :defaultColDef="defaultColDef"
          :grid-options="gridOptions"
          :suppressDragLeaveHidesColumns="true"
          :rowData="apiLogsList"
        ></AgGridVue>
      </v-col>
    </template>
    <template #dialogs>
      <OutboundIntegrationLogFilters
        :bindingObject="filters"
        v-model="showFiltersDialog"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(15)"
      />
      <RenewConfirmationDialogue
        v-model="showConfirmationDialogue"
        :status="status"
        :title="title"
        :postDate="postDate"
        :text="text"
        :showRemarks="false"
        @confirmed="submit"
        @rejected="refreshCalls()"
      />
    </template>
  </ListComponent>
</template>

<script>
import Columns from "@/components/General/Columns.vue";
import ListComponent from "@/components/General/ListComponent.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import IntegrationLogButton from "@/components/AgGridComponents/IntegrationLogButton.vue";
import OutboundIntegrationLogFilters from "@/components/ModuleBased/dialogs/filters/OutboundIntegrationLogFilters.vue";
import RenewConfirmationDialogue from "@/components/General/RenewConfirmationDialogue.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    ListComponent,
    Columns,
    AgGridVue,
    Pagination,
    agColumnHeader: CustomHeader,
    DetailsParent,
    IntegrationLogButton,
    OutboundIntegrationLogFilters,
    RenewConfirmationDialogue,
    DateField,
  },
  watch: {
    tab() {
      this.pageNo = 1;
      this.apiCall();
    },
    showExportDialog(isOpen) {
      if (!isOpen) {
        this.exportDateFrom = null;
        this.exportDateTo = null;
      }
    },
  },
  data() {
    return {
      sorting: {},
      showFiltersDialog: false,
      showConfirmationDialogue: false,
      showExportDialog: false,
      exporting: false,
      exportDateFrom: null,
      exportDateTo: null,
      postDate: null,
      status: "secondery",
      title: "Confirmation",
      text: "Are you sure you want to send to SAP?",
      headers: [
        {
          headerName: "Action On",
          field: "created",
          tooltipField: "created",
          sortable: true,
          minWidth: 120,
          valueFormatter: (params) => {
            return this.$globals.formatDate(params.value, true);
          },
        },
        {
          headerName: "Method",
          field: "method",
          tooltipField: "method",
          sortable: true,
          minWidth: 120,
        },
        {
          headerName: "Detail",
          field: "detail",
          tooltipField: "detail",
          sortable: true,
          minWidth: 120,
          valueFormatter: (params) => {
            return params.data.detail ? params.data.detail : "N/A";
          },
        },
        {
          headerName: "Post Date",
          field: "post_date",
          sortable: true,
          minWidth: 70,
          valueFormatter: (params) => {
            return params.data.post_date ? params.data.post_date : "N/A";
          },                                        
        },
        {
          headerName: "Status",
          field: "status",
          tooltipField: "last_name",
          minWidth: 120,
          cellRenderer: function (params) {
            const result = /^2/.test(params.data.response_code.toString())
              ? `<span class="success-border px-2 py-1 success--text"> Success </span>`
              : `<span class="error-border px-2 py-1 error--text"> Failed </span>`;
            return result;
          },
        },
        {
          headerName: "Actions",
          field: "actions",
          cellRendererFramework: "IntegrationLogButton",
          pinned: "right",
          width: 120,
        },
      ],
      apiLogsList: [],
      // pagination vars
      itemsPerPage: 20,
      pageNo: 1,
      totalItems: 0,
      sapData: {},

      // Filter vars
      filters: {},

      tab: null,
      permissions: null,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        resizable: true,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },
    };
  },
  computed: {
    headersForTable() {
      let headerForTabGeneral = [...this.headers];

      if (this.tab == 0) {
        return [
          ...headerForTabGeneral,
          {
            headerName: "Invoice No",
            field: "invoice_no",
            tooltipField: "invoice_no",
            sortable: true,
            minWidth: 120,
          },
        ];
      } else if (this.tab == 1) {
        return [
          ...headerForTabGeneral,

          {
            headerName: "Payment Receipt No",
            field: "receipt_id",
            tooltipField: "receipt_id",
            sortable: true,
            minWidth: 120,
          },
        ];
      } else if (this.tab == 2) {
        return [
          ...headerForTabGeneral,

          {
            headerName: "Sub Hire ID",
            field: "sub_hire_id",
            tooltipField: "invoice_no",
            sortable: true,
            minWidth: 120,
          },
        ];
      } else if (this.tab == 3) {
        return [
          ...headerForTabGeneral,
          {
            headerName: "Credit Note No",
            field: "credit_note_no",
            tooltipField: "credit_note_no",
            sortable: true,
            minWidth: 120,
          },
        ];
      } else if (this.tab == 4) {
        return [
          ...headerForTabGeneral,
          {
            headerName: "Debit Note No",
            field: "debit_note_no",
            tooltipField: "debit_note_no",
            sortable: true,
            minWidth: 120,
          },
        ];
      } 
      else if (this.tab == 5) {
        return [
          ...headerForTabGeneral,
          {
            headerName: "Payout ID",
            field: "sap_payout_id",
            tooltipField: "sap_payout_id",
            sortable: true,
            minWidth: 120,
          },
        ];
      } else {
        return [
          ...headerForTabGeneral,
          {
            headerName: "VBS Reference No",
            field: "vbs_reference_number",
            tooltipField: "vbs_reference_number",
            sortable: true,
            minWidth: 150,
            valueFormatter: (params) => params.data.vbs_reference_number || "N/A",
          },
          {
            headerName: "Sub Hire ID",
            field: "sub_hire_id",
            tooltipField: "sub_hire_id",
            minWidth: 140,
            valueFormatter: (params) => params.data.sub_hire_id || "N/A",
          },
          {
            headerName: "Integration ID",
            field: "integration_id",
            tooltipField: "integration_id",
            sortable: true,
            minWidth: 160,
          },
        ];
      }
    },
    context() {
      return { parentComponent: this };
    },
    currentProfile() {
      let obj = JSON.parse(localStorage.getItem("userProfile"));
      if (!obj) {
        return null;
      } else {
        return obj;
      }
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  methods: {
    refreshCalls() {
      this.apiCall();
    },
    resendSAPData(sapData) {
    
      this.showConfirmationDialogue = true;
      this.postDate = sapData.post_date;
      this.sapData = sapData;
    },

    submit(data) {
      this.sapDataMethod(this.sapData, data.postDate);
    },

    sapDataMethod(sapData, postDate) {
      this.$bus.$emit("showLoader", true);
      let payload = {};

      payload.post_date = postDate;
      payload.integration_log_id = sapData.id;

      if (sapData.hasOwnProperty("invoice_no")) {
        payload.invoice_no = sapData.invoice_no;
      } else if (sapData.hasOwnProperty("receipt_id")) {
        payload.receipt_id = sapData.receipt_id;
      } else if (sapData.hasOwnProperty("sub_hire_id")) {
        payload.sub_hire_id = sapData.sub_hire_id;
      } else if (sapData.hasOwnProperty("emp_id")) {
        payload.sub_hire_id = sapData.emp_id;
      } else if (sapData.hasOwnProperty("debit_note_no")) {
        payload.debit_note = sapData.debit_note_no;
      } 
      else {
        payload.credit_note_no = sapData.credit_note_no;
      }
      this.$api.integration
        .sentDataToSAP(payload)
        .then(() => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Sent to SAP successfully!",
            color: "success",
          });
          this.apiCall();
        })
        .catch((err) => {
          this.$bus.$emit("showToastMessage", {
            message: `${
              err.data && err.data.non_field_errors
                ? err.data.non_field_errors.join()
                : "Cannot send to SAP"
            }`,
            color: "error",
          });
          this.$bus.$emit("showLoader", false);
        });
    },

    // resendSAPData(sapData) {
    //   const config = {
    //     status: "secondary",
    //     title: "Sent To SAP",
    //     text: "Are you sure you want to sent to SAP?",
    //   };

    //   this.$confirmed(config).then(() => {
    //     this.$bus.$emit("showLoader", true);
    //     let payload = {};
    //     if (sapData.hasOwnProperty("invoice_no")) {
    //       payload.invoice_no = sapData.invoice_no;
    //     } else if (sapData.hasOwnProperty("receipt_id")) {
    //       payload.receipt_id = sapData.receipt_id;
    //     } else if (sapData.hasOwnProperty("sub_hire_id")) {
    //       payload.sub_hire_id = sapData.sub_hire_id;
    //     } else {
    //       payload.credit_note_no = sapData.credit_note_no;
    //     }

    //     this.$api.integration
    //       .sentDataToSAP(payload)
    //       .then((resp) => {
    //         this.$bus.$emit("showLoader", false);
    //         this.$bus.$emit("showToastMessage", {
    //           message: "Send To Sap!",
    //           color: "success",
    //         });
    //         this.apiCall();
    //       })
    //       .catch((err) => {
    //         this.$bus.$emit("showToastMessage", {
    //           message: `${
    //             err.data && err.data.non_field_errors
    //               ? err.data.non_field_errors.join()
    //               : "Cannot Sent To SAP"
    //           }`,
    //           color: "error",
    //         });
    //         this.$bus.$emit("showLoader", false);
    //       });
    //   });
    // },
    apiCall() {
      if (this.tab === 0) {
        this.getCustomerInvoiceIntegrationLogs();
      } else if (this.tab === 1) {
        this.getPaymentReceiptIntegrationLogs();
      } else if (this.tab === 2) {
        this.getSubHireIntegrationLogs();
      } else if (this.tab === 3) {
        this.getCreditNoteIntegrationLogs();
      } else if (this.tab === 4) {
        this.getDebitNoteIntegrationLogs();
      } else if (this.tab === 5) {
        this.getSapDriverIntegrationLogs();
      } else if (this.tab === 6) {
        this.getTosOutboundLogs();
      } else if (this.tab === 7) {
        this.getCodecoOutboundLogs();
      } else {
        this.getTosControlTowerDashboardLogs();
      }
    },
    resizeGrid() {
      if (this.gridOptions && this.gridOptions.api) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(15);
    },
    searchLogs() {
      this.pageNo = 1;
      this.apiCall();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.apiCall();
    },
    prevPage() {
      this.pageNo--;
      this.apiCall();
    },
    nextPage() {
      this.pageNo++;
      this.apiCall();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.itemsPerPageChanged(20);
    },

    getCustomerInvoiceIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      if (this.filters.status === "Success") {
        this.filters.status_code = "200, 201";
      } else if (this.filters.status === "Failed") {
        this.filters.status_code = "400, 500, 503";
      }
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getCustomerInvoiceIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getPaymentReceiptIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      if (this.filters.status === "Success") {
        this.filters.status_code = "200, 201";
      } else if (this.filters.status === "Failed") {
        this.filters.status_code = "400, 500, 503";
      }
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getPaymentReceiptIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getCreditNoteIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      if (this.filters.status === "Success") {
        this.filters.status_code = "200, 201";
      } else if (this.filters.status === "Failed") {
        this.filters.status_code = "400, 500, 503";
      }
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getCreditNoteIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

     getDebitNoteIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      if (this.filters.status === "Success") {
        this.filters.status_code = "200, 201";
      } else if (this.filters.status === "Failed") {
        this.filters.status_code = "400, 500, 503";
      }
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getDebitNoteIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },


    getSubHireIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      if (this.filters.status === "Success") {
        this.filters.status_code = "200, 201";
      } else if (this.filters.status === "Failed") {
        this.filters.status_code = "400, 500, 503";
      }

      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getSubHireIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    exportData() {
      this.exporting = true;
      const params = { ...this.filters };
      if (this.exportDateFrom) params.created_after = this.exportDateFrom;
      if (this.exportDateTo) params.created_before = this.exportDateTo;
      this.$api.integration
        .exportSubHireIntegrationLogs(params)
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Sub_Hire_Integration_Logs_${new Date().getTime()}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
          this.exporting = false;
          this.showExportDialog = false;
        })
        .catch((err) => {
          this.exporting = false;
          this.$bus.$emit("showToastMessage", {
            message:
              (err.data && err.data.detail) || "Failed to export data",
            color: "error",
          });
        });
    },

    getSapDriverIntegrationLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      if (this.filters.status === "Success") {
        this.filters.status_code = "200, 201";
      } else if (this.filters.status === "Failed") {
        this.filters.status_code = "400, 500, 503";
      }

      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getSapIntegrationLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getTosOutboundLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getTosOutboundLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getTosControlTowerDashboardLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getTosControlTowerDashboardLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getCodecoOutboundLogs(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.integration
        .getCodecoOutboundLogs(params)
        .then((res) => {
          this.apiLogsList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("integration");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.permissions = { ...permissions };
        this.apiCall();
      }
    }
  },
};
</script>

<style>
.success-border {
  border: 1px solid #4caf50;
  border-radius: 30px;
}

.error-border {
  border: 1px solid #ff5252;
  border-radius: 30px;
}
</style>
